import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { X, Search } from 'lucide-react';
import { businessSupplierService, businessMappingService } from '../../../services/businessApi';
import '../Tickets/NewBusinessTicket.css'; // .supplier-selected / .supplier-results

const initialFields = { startDate: '', firstAsnDate: '', newCode: '', pocName: '', pocPhone: '', pocEmail: '', notes: '' };

// Manual "add supplier to plant" (Task 2.6's alternative to CSV import) — search-only.
// Supplier creation itself lives on the Suppliers page (the one canonical place to create
// a supplier); onboarding one onto a plant's checklist assumes you already know who they are.
export default function AddSupplierModal({ plantId, plantName, onClose, onSaved }) {
  const [supplierQuery, setSupplierQuery] = useState('');
  const [supplierResults, setSupplierResults] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [fields, setFields] = useState(initialFields);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (selectedSupplier || supplierQuery.trim().length < 2) { setSupplierResults([]); return; }
    const t = setTimeout(() => {
      businessSupplierService.search(supplierQuery).then(res => setSupplierResults(res.data.suppliers));
    }, 300);
    return () => clearTimeout(t);
  }, [supplierQuery, selectedSupplier]);

  const updateField = (field) => (e) => setFields(f => ({ ...f, [field]: e.target.value }));

  const handleSave = async () => {
    setError('');
    if (!selectedSupplier) {
      setError('Select a supplier');
      return;
    }

    setSaving(true);
    try {
      await businessMappingService.create({
        plantId,
        supplierId: selectedSupplier._id,
        startDate: fields.startDate || undefined,
        firstAsnDate: fields.firstAsnDate || undefined,
        newCode: fields.newCode || undefined,
        poc: { name: fields.pocName, contactPhone: fields.pocPhone, contactEmail: fields.pocEmail },
        notes: fields.notes || undefined,
      });
      onSaved();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add supplier');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="drawer-overlay" onClick={onClose}>
      <div className="client-form-modal" style={{ margin: 'auto', width: 480, maxHeight: '85vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Add Supplier — {plantName}</h3>
          <button className="icon-btn-sm" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="client-form">
          {error && <div className="form-error">{error}</div>}

          <div className="form-section-title">Supplier</div>
          {selectedSupplier ? (
            <div className="supplier-selected">
              <div>
                <div className="checklist-supplier-name">{selectedSupplier.name}</div>
                <div className="checklist-supplier-code">{selectedSupplier.vendorCode}</div>
              </div>
              <button type="button" className="icon-btn-sm" onClick={() => setSelectedSupplier(null)}><X size={14} /></button>
            </div>
          ) : (
            <>
              <div className="search-box">
                <Search size={14} />
                <input
                  placeholder="Search supplier by name or vendor code..."
                  value={supplierQuery}
                  onChange={e => setSupplierQuery(e.target.value)}
                />
              </div>
              {supplierResults.length > 0 && (
                <div className="supplier-results">
                  {supplierResults.map(s => (
                    <div key={s._id} className="supplier-result-row" onClick={() => { setSelectedSupplier(s); setSupplierResults([]); }}>
                      <span>{s.name}</span>
                      <span className="checklist-supplier-code">{s.vendorCode}</span>
                    </div>
                  ))}
                </div>
              )}
              <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                Not listed? <Link to="/business/suppliers" onClick={onClose}>Add them as a new supplier first</Link>.
              </p>
            </>
          )}

          <div className="form-section-title">Plant-specific details (optional)</div>
          <div className="form-row">
            <div className="form-group">
              <label>New Code (if different for this plant)</label>
              <input value={fields.newCode} onChange={updateField('newCode')} />
            </div>
            <div className="form-group">
              <label>Start date</label>
              <input type="date" value={fields.startDate} onChange={updateField('startDate')} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>First ASN date</label>
              <input type="date" value={fields.firstAsnDate} onChange={updateField('firstAsnDate')} />
            </div>
            <div className="form-group">
              <label>POC name</label>
              <input value={fields.pocName} onChange={updateField('pocName')} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>POC phone</label>
              <input value={fields.pocPhone} onChange={updateField('pocPhone')} />
            </div>
            <div className="form-group">
              <label>POC email</label>
              <input type="email" value={fields.pocEmail} onChange={updateField('pocEmail')} />
            </div>
          </div>
          <div className="form-group">
            <label>Comments</label>
            <textarea rows={2} value={fields.notes} onChange={updateField('notes')} />
          </div>

          <div className="modal-footer">
            <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving || !selectedSupplier}>
              {saving ? 'Adding...' : 'Add to Plant'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
